import { useState } from 'react'
import { useStatusSetzen, useZurueckgeben } from '../../lib/mutationen'
import { Button } from '../ui/primitives'

/**
 * **Der Rückweg, der die Angst nimmt**
 * ([Z-19](../../../../../docs/phasen/zusatz-19-tagesplan.md) AP 19.10).
 *
 * ## Warum dieses Blatt das eigentliche Arbeitspaket ist
 *
 * Z-19 fing mit einem Satz von Nils an, der wie eine Entschuldigung klang und in Wahrheit eine
 * Diagnose war:
 *
 * > *„Ich denke ich habe Angst das es im backlog untergeht daher schon in Bearbeitung gemacht."*
 *
 * Zwölf Aufgaben standen auf „in Arbeit", acht davon seit fünf bis zwölf Tagen unberührt.
 * **„In Arbeit" war kein Status, sondern ein Lesezeichen gegen das Vergessen** – und zwar ein
 * vernünftiges: Wer dem Rückweg nicht trauen kann, legt nichts zurück.
 *
 * Daraus folgt die Reihenfolge, in der dieses Zusatzpaket gebaut werden musste. Eine Schleuse,
 * die den Zugang begrenzt, ohne dass der Ausgang verlässlich ist, macht die Lage **schlimmer**:
 * Sie zwingt zu einer Entscheidung, deren eine Hälfte man nicht treffen will. Erst der
 * verlässliche Rückweg macht die Grenze zumutbar. Die Phasendatei sagt es kürzer: *„Ohne es
 * plant man nur schöner."*
 *
 * ## Ohne Antwort geht nichts hinaus
 *
 * Deshalb hat dieses Blatt **keinen stillen Ausgang**. „Abbrechen" tut nichts – die Aufgabe
 * bleibt, wo sie war. Wer sie wirklich aus der Hand gibt, beantwortet vorher eine einzige
 * Frage, und beide Antworten sind legitim:
 *
 * | Antwort | Was passiert |
 * |---|---|
 * | **Jemand anders macht sie** | zurück in den Pool, aus dem sie kam – sichtbar für das Team |
 * | **Ich, später** | `warten` mit Wiedervorlage; sie steht an dem Tag wieder in „Als Nächstes" |
 *
 * **Das ist der ganze Trick**: Die zweite Antwort gibt es seit dem 02.09.2026
 * ([E-357](../../../../../docs/ENTSCHEIDUNGEN.md#e-357)) und sie funktionierte auch – sie war
 * nur ein Weg unter vielen, versteckt in einem Statusmenü. Hier wird sie zum **Regelweg**: Sie
 * ist das, was man beim Zurücklegen als Erstes sieht.
 *
 * ## Warum keine neue Prozedur
 *
 * Weder `zurueckgeben` noch `statusSetzen` ändern sich. Beides gab es, beides tut das Richtige;
 * gefehlt hat nur die Stelle, an der die Frage **gestellt** wird. Eine dritte Prozedur hätte
 * dieselbe Wirkung ein zweites Mal beschrieben – und die Regel wäre ab dann an zwei Orten zu
 * pflegen gewesen.
 */

/** Die angebotenen Abstände – mehr Auswahl heißt nur längeres Überlegen. */
const ABSTAENDE: { tage: number; text: string }[] = [
  { tage: 1, text: 'morgen' },
  { tage: 3, text: 'in 3 Tagen' },
  { tage: 7, text: 'nächste Woche' },
  { tage: 14, text: 'in 2 Wochen' },
]

/** `2026-10-15` in Ortszeit – über UTC spränge der Tag für alle westlich von Greenwich. */
function tagIn(tage: number): string {
  const d = new Date()
  d.setDate(d.getDate() + tage)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function Wiedersehen({
  aufgabeId,
  titel,
  schliessen,
  weg,
}: {
  aufgabeId: string
  titel: string
  schliessen: () => void
  /**
   * **Nur, wenn sie wirklich hinausgeht** – nicht bei „Abbrechen". Der Fokus-Puls (Z-22) lässt die
   * Aufgabe dann los; ein bloßes Schließen des Blattes soll sie im Puls lassen.
   */
  weg?: () => void
}) {
  const status = useStatusSetzen()
  const zurueck = useZurueckgeben()
  const [eigenes, setEigenes] = useState('')
  const laeuft = status.isPending || zurueck.isPending

  const spaeter = (tag: string) => {
    status.mutate({ id: aufgabeId, status: 'warten', wartenBis: tag })
    weg?.()
    schliessen()
  }

  return (
    <div
      /*
       * `unter-leiste` statt `inset-0`: Die NetCon-Kopfleiste klebt oben mit 48 px und `z-[90]`.
       * Eine Ebene über das ganze Fenster läge darunter – auf dem Telefon verschwände das obere
       * Ende des Blattes dahinter (E-325). Der Wächter `unter-leiste.test.ts` hat genau das hier
       * gemeldet, bevor es jemand gesehen hat.
       */
      className="unter-leiste fixed inset-x-0 bottom-0 z-[60] flex items-end justify-center bg-[rgb(23_22_20/0.28)] sm:items-center sm:justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Wann siehst du sie wieder?"
      onClick={schliessen}
    >
      {/* Das Blatt kommt auf dem Telefon von unten – dort liegt der Daumen (Z-16). */}
      <div
        /* Höhe begrenzt, Rumpf scrollt – sonst liegt der Fuß auf einem kleinen Telefon unter
           dem Bildrand und „Abbrechen" wäre nicht erreichbar (E-325). */
        className="flex max-h-[min(80vh,100%)] w-full max-w-[420px] flex-col rounded-t-card border border-line bg-surface shadow-lg sm:rounded-card"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="border-b border-line px-4 py-3 text-[14.5px] font-bold">
          Wann siehst du sie wieder?
        </h3>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          <p className="mb-1 truncate text-[13px] font-medium" title={titel}>
            {titel}
          </p>
          <p className="mb-3 text-[12.5px] text-ink2">
            Ohne Antwort geht sie nicht hinaus – das ist der Unterschied zum stillen Ablegen.
          </p>

          <div className="mb-3 flex flex-wrap gap-1.5">
            {ABSTAENDE.map(({ tage, text }) => (
              <Button
                key={tage}
                variant="ghost"
                disabled={laeuft}
                onClick={() => spaeter(tagIn(tage))}
              >
                {text}
              </Button>
            ))}
          </div>

          <label className="flex items-center gap-2 text-[12.5px] text-ink2">
            oder am
            <input
              type="date"
              value={eigenes}
              onChange={(e) => setEigenes(e.target.value)}
              className="rounded-lg border border-line2 px-2 py-1 text-[12.5px]"
            />
            <Button
              variant="ghost"
              disabled={laeuft || eigenes === ''}
              onClick={() => spaeter(eigenes)}
            >
              merken
            </Button>
          </label>
        </div>

        {/**
         * **Die zweite legitime Antwort.** Sie steht abgesetzt, weil sie etwas anderes bedeutet:
         * nicht „später ich", sondern „nicht ich". Beides ist in Ordnung – was nicht in Ordnung
         * ist, ist das stille Liegenlassen, das keine von beiden Aussagen trifft.
         */}
        <div className="border-t border-line px-4 py-3">
          <Button
            variant="ghost"
            disabled={laeuft}
            onClick={() => {
              zurueck.mutate(aufgabeId)
              weg?.()
              schliessen()
            }}
            title="Zurück in den Pool, aus dem sie kam – Zuweisung und Status wie vorher"
          >
            ← Jemand anders macht sie
          </Button>
        </div>

        <footer className="flex justify-end border-t border-line px-4 py-2.5">
          <Button variant="ghost" onClick={schliessen}>
            Abbrechen
          </Button>
        </footer>
      </div>
    </div>
  )
}
